import { Component, Inject, OnInit } from '@angular/core';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import icClose from '@iconify/icons-ic/twotone-close';
import icWarning from '@iconify/icons-ic/twotone-warning';
import { PurchaseActorService } from '../../../services/purchase-actor.service';
import { UserProfileService } from '../../../../pages/application/management/user-profile/user-profile.service';
import { SnackbarService } from '../../../services/snackbar.service';
import {
  PURCHASE_ACTOR_ALL_ROLES,
  PURCHASE_ACTOR_ROLE_FILTER_FLAG,
  PURCHASE_ACTOR_ROLE_LABELS,
  PurchaseActorAssignableContact,
  PurchaseActorDependencies,
  PurchaseActorRoleEnum,
} from '../../../models/purchase-actor.model';

export interface PurchaseActorContactReplacementModalData {
  contact: { _id: string; firstName: string; lastName: string };
  dependencies: PurchaseActorDependencies;
  /** Limite le remplacement aux docs `scope: establishment` de cet établissement (cf. payload back). */
  establishmentId?: string;
}

/**
 * Modale affichée à la suppression d'un contact référencé comme acteur achat (6 rôles possibles,
 * PRED-3139) — adaptation multi-rôles de `DefaultSupervisionContactReplacementModalComponent`
 * (même structure/UX : impacts informatifs, puis choix remplacer/retirer).
 *
 * Différences avec la modale supervision (mono-rôle) :
 * - Les dépendances (`data.dependencies.data[].dependencies`) sont des chaînes
 *   `"Famille (Rôle A, Rôle B)"` — un contact peut tenir PLUSIEURS rôles à la fois, sur
 *   plusieurs familles/établissements différents.
 * - L'autocomplete de remplacement est filtrée sur l'UNION des rôles tenus par le contact
 *   sortant, fournie de façon STRUCTURÉE par le back (`dependencies.rolesHeld`, PRED-3139 code
 *   review — évite le re-parsing fragile des libellés dans les chaînes de dépendances, qui
 *   produisait un faux positif si une catégorie en texte libre contenait un mot de rôle, ex.
 *   « Acheteurs matériel »). Chaque rôle requis ajoute son flag `PURCHASE_ACTOR_ROLE_FILTER_FLAG`
 *   à l'appel `UserProfileService.findAll`.
 * - Pas de notion de "mode de propagation" (ONLY_DEFAULT/ALL) : `purchase-actor` n'a pas
 *   d'équivalent des Metrik à superviseur saisi manuellement — le back remplace le contact dans
 *   CHAQUE doc/rôle où il figure, un point.
 *
 * Finding (cf. brief T16) : `UserProfileFilteringService` (back) pousse chaque flag
 * (`isApprover`, `isPurchaser`, `isValidator`, `isReferentEngagement`, `isSupervision`) comme une
 * condition `{'contactType.label': X}` séparée dans `composite.andMatch`, et ces conditions sont
 * combinées avec `$and` (cf. `aggregate.helper.service.ts`). Passer PLUSIEURS flags dans un même
 * appel `findAll` restreint donc DÉJÀ aux users portant TOUS les contactTypes demandés (ET, pas
 * OU) — aucun filtrage client-side supplémentaire n'est nécessaire pour obtenir "porte TOUS les
 * rôles requis". C'est d'ailleurs exactement la même règle que le back applique côté validation
 * (`validateReplacementContact` exige que `newContact` porte l'union des rôles remplacés).
 */
@Component({
  selector: 'purchase-actor-contact-replacement-modal',
  templateUrl: './purchase-actor-contact-replacement-modal.component.html',
})
export class PurchaseActorContactReplacementModalComponent implements OnInit {
  icClose = icClose;
  icWarning = icWarning;

  form: UntypedFormGroup;
  contacts$: Observable<PurchaseActorAssignableContact[]>;
  loading = false;

  /** Rôles tenus par le contact sortant (union, tous impacts confondus) — déduits des chaînes de
   * dépendances. Affichés dans la modale pour expliquer le filtrage de l'autocomplete. */
  requiredRoles: PurchaseActorRoleEnum[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: PurchaseActorContactReplacementModalData,
    public dialogRef: MatDialogRef<PurchaseActorContactReplacementModalComponent>,
    private purchaseActorService: PurchaseActorService,
    private userProfileService: UserProfileService,
    private snackbarService: SnackbarService,
  ) {}

  get contactName(): string {
    return `${this.data.contact.firstName} ${this.data.contact.lastName}`;
  }

  get requiredRoleLabels(): string {
    return this.requiredRoles.map(role => PURCHASE_ACTOR_ROLE_LABELS[role]).join(', ');
  }

  ngOnInit(): void {
    this.form = new UntypedFormGroup({
      choice: new UntypedFormControl('replace'),
      contact: new UntypedFormControl(null),
    });
    this.requiredRoles = this.extractRequiredRoles();
    this.autocompleteContacts();
  }

  /**
   * Détermine les rôles requis pour filtrer l'autocomplete de remplacement.
   * Priorité au champ structuré `dependencies.rolesHeld` (back, PRED-3139 code review).
   * Fallback DÉFENSIF uniquement (réponse d'un back plus ancien / non encore redéployé, ou
   * endpoint partageant le contrat générique sans le champ) : re-parse les libellés de
   * `PURCHASE_ACTOR_ROLE_LABELS` dans les chaînes de dépendances (`"Famille (Rôle A, Rôle B)"`)
   * — fragile, un libellé de catégorie en texte libre contenant un mot de rôle produit un faux
   * positif. À supprimer une fois la garantie du champ structuré actée pour tous les appelants.
   */
  private extractRequiredRoles(): PurchaseActorRoleEnum[] {
    if (this.data.dependencies?.rolesHeld) {
      return [...this.data.dependencies.rolesHeld];
    }

    const lines = (this.data.dependencies?.data ?? []).flatMap(item => item.dependencies ?? []);
    const roles = new Set<PurchaseActorRoleEnum>();
    for (const line of lines) {
      for (const role of PURCHASE_ACTOR_ALL_ROLES) {
        if (line.includes(PURCHASE_ACTOR_ROLE_LABELS[role])) {
          roles.add(role);
        }
      }
    }
    return [...roles];
  }

  autocompleteContacts(search = ''): void {
    const flags: Record<string, unknown> = {};
    for (const role of this.requiredRoles) {
      flags[PURCHASE_ACTOR_ROLE_FILTER_FLAG[role]] = true;
    }
    this.contacts$ = this.userProfileService
      .findAll(null, null, 'fullName', 'asc', search, {
        ...flags,
        isPredik: false,
        excludedIds: [this.data.contact._id],
      })
      .pipe(map(result => result.data));
  }

  confirm(): void {
    const isReplace = this.form.value.choice === 'replace';
    const newContact: PurchaseActorAssignableContact = this.form.value.contact;

    if (isReplace && !newContact?._id) {
      this.form.get('contact').markAsTouched();
      this.snackbarService.warning('Sélectionnez le contact de remplacement');
      return;
    }

    this.loading = true;
    this.purchaseActorService
      .replaceContact({
        oldContactId: this.data.contact._id,
        newContact: isReplace
          ? { _id: newContact._id, firstName: newContact.firstName, lastName: newContact.lastName }
          : undefined,
        establishmentId: this.data.establishmentId,
      })
      .subscribe({
        next: () => {
          this.snackbarService.success(
            isReplace ? 'Acteur achat remplacé avec succès' : 'Affectations retirées avec succès',
          );
          this.dialogRef.close(true);
        },
        error: () => {
          this.snackbarService.danger('Une erreur est survenue lors du remplacement du contact');
          this.loading = false;
        },
      });
  }
}
